import http from '@homeservices/api-kit/axios';

// The inbox returns the latest message of each conversation.
// Its service_provider_id is unreliable, so the Pro is identified by user id: the other side of
// the message. It equals the work order match's service_provider_user_identifier.
export const getProUserId = message => (
  message.sender_type === 'pro' ? message.sender_id : message.recipient_id
);

// Opening a conversation marks its Pro messages as seen on the server (see markConversationRead).
// The ids are also kept in localStorage so "New" clears instantly and stays cleared if that call
// fails; the server is the source of truth for a fresh browser session.
export const READ_IDS_KEY = 'consumer-inbox-read-message-ids';
const MAX_READ_IDS = 500;

const loadReadIds = () => {
  try {
    return JSON.parse(window.localStorage.getItem(READ_IDS_KEY)) || [];
  } catch (e) {
    return [];
  }
};

const saveReadIds = (ids) => {
  try {
    window.localStorage.setItem(READ_IDS_KEY, JSON.stringify(ids));
  } catch (e) {
    // Storage unavailable (private mode, SSR): read state lasts for the session only
  }
};

const isUnreadProMessage = (message, readIds) => message.sender_type === 'pro'
  && message.message_type !== 'system'
  && !message.time_read
  && message.order_hash !== null
  && !readIds.includes(message.message_id);

const SEE_URL = (orderHash, messageId) => `/platform/workorder/r/workorder/prorders/messages/order-hash/${orderHash}/message-id/${messageId}/actions/see/v1`;

// A Pro message the server still reports as unread
const isUnseenOnServer = message => message.sender_type === 'pro'
  && message.message_type !== 'system'
  && !message.time_read;

// Inbox fetches wait for in-flight "seen" calls, otherwise the server still reports unread
let pendingReads = Promise.resolve();

export default {
  namespaced: true,
  state () {
    return {
      postedSeenIds: [],
      readMessageIds: loadReadIds(),
      inbox: [],
      count: 0,
      total: 0,
      unreadMessages: [],
    };
  },
  getters: {
    getInbox: state => state.inbox,
    getCount: state => state.count,
    getTotal: state => state.total,
    unreadMessages: state => state.unreadMessages,
    hasUnreadMessages: state => state.unreadMessages.length > 0,
    // jobId is the work order's user_friendly_identifier (what the dashboard/routes use)
    hasUnreadMessage: state => (jobId, proUserId) => state.unreadMessages.some(
      message => message.user_friendly_identifier === jobId
        && String(getProUserId(message)) === String(proUserId)
    ),
  },
  actions: {
    async fetchInbox ({ state, commit }, { params = {}, reason } = {}) {
      await pendingReads;
      const { data } = await http.get('/platform/workorder/r/workorder/prorders/messages/customer/inbox/v1', {
        params: {
          size: '20',
          ...params,
        },
      });
      const results = data.results.filter(message => message.order_hash !== null);
      const unreadResults = results.filter(m => isUnreadProMessage(m, state.readMessageIds));
      if (reason === 'list') {
        commit('addInbox', results);
        commit('setTotal', data.total);
        commit('setCount', data.count);
        // First page replaces unread; later pages add to it
        commit('setUnreadMessages', params.from
          ? [...state.unreadMessages, ...unreadResults]
          : unreadResults);
      } else {
        commit('setUnreadMessages', unreadResults);
      }
    },
    async refreshInbox ({ commit, dispatch }) {
      commit('clearInbox');
      await dispatch('fetchInbox', { reason: 'list' });
    },
    // Opening a conversation (list item or "Message" button) marks it read
    markAsRead ({ state, commit }, { jobId, proUserId }) {
      const isTarget = message => message.user_friendly_identifier === jobId
        && String(getProUserId(message)) === String(proUserId);
      const targets = state.unreadMessages.filter(isTarget);
      if (!targets.length) { return; }

      commit('addReadMessageIds', targets.map(message => message.message_id));
      commit('setUnreadMessages', state.unreadMessages.filter(message => !isTarget(message)));
    },
    // Chat opened: every unread Pro message loaded in it is marked as seen, locally and on the
    // server. service_provider_id is required by the API for a consumer session.
    async markConversationRead ({ state, commit, dispatch }, {
      messages, jobId, proUserId, skillsProfileId,
    }) {
      dispatch('markAsRead', { jobId, proUserId });
      const unread = messages.filter(message => isUnseenOnServer(message)
        && !state.postedSeenIds.includes(message.message_id));
      if (!unread.length) { return; }

      const ids = unread.map(message => message.message_id);
      commit('addReadMessageIds', ids);
      commit('addPostedSeenIds', ids);
      pendingReads = Promise.all([pendingReads, ...unread.map(message => http.post(
        SEE_URL(message.order_hash, message.message_id),
        // No body (like the Pro side): a null body gets a form content-type the API rejects
        undefined,
        { params: { service_provider_id: skillsProfileId } }
      ).catch((e) => {
        commit('removePostedSeenId', message.message_id);
        console.error(e); // eslint-disable-line no-console
      }))]);
      await pendingReads;
    },
  },
  mutations: {
    addPostedSeenIds (state, ids) {
      state.postedSeenIds = [...state.postedSeenIds, ...ids];
    },
    removePostedSeenId (state, id) {
      state.postedSeenIds = state.postedSeenIds.filter(seenId => seenId !== id);
    },
    addReadMessageIds (state, ids) {
      state.readMessageIds = [...new Set([
        ...loadReadIds(),
        ...state.readMessageIds,
        ...ids,
      ])].slice(-MAX_READ_IDS);
      saveReadIds(state.readMessageIds);
    },
    addInbox (state, data) {
      state.inbox = [...state.inbox, ...data];
    },
    clearInbox (state) {
      state.inbox = [];
    },
    setCount (state, data) {
      state.count = data;
    },
    setTotal (state, data) {
      state.total = data;
    },
    setUnreadMessages (state, data) {
      state.unreadMessages = data;
    },
  },
};
